import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Shield, Database, Globe, Activity, Download, Upload, RefreshCw, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const BASE = process.env.REACT_APP_BACKEND_URL || '';

async function apiGet(path) {
  const token = localStorage.getItem('access_token');
  const r = await fetch(`${BASE}${path}`, { headers: { Authorization: `Bearer ${token}` } });
  return r.json();
}

async function apiPost(path, body = {}) {
  const token = localStorage.getItem('access_token');
  const r = await fetch(`${BASE}${path}`, { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
  return r.json();
}

export default function AdminSystemDR() {
  const [backups, setBackups] = useState([]);
  const [runbook, setRunbook] = useState(null);
  const [multiRegion, setMultiRegion] = useState(null);
  const [obsConfig, setObsConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creatingBackup, setCreatingBackup] = useState(false);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [bkp, rb, mr, obs] = await Promise.all([
        apiGet('/api/system/backups').catch(() => ({ backups: [] })),
        apiGet('/api/system/dr-runbook').catch(() => null),
        apiGet('/api/system/health/multi-region').catch(() => null),
        apiGet('/api/system/observability-config').catch(() => null),
      ]);
      setBackups(bkp.backups || []);
      setRunbook(rb);
      setMultiRegion(mr);
      setObsConfig(obs);
    } catch {}
    finally { setLoading(false); }
  };

  const createBackup = async () => {
    setCreatingBackup(true);
    try {
      const r = await apiPost('/api/system/backup', { storage: 'local' });
      toast.success(`Backup created: ${r.backup_id}`);
      loadAll();
    } catch { toast.error('Backup failed'); }
    finally { setCreatingBackup(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Shield className="w-6 h-6 text-[#B89B5E]" /> Disaster Recovery & Observability</h1>
          <p className="text-sm text-[#5B616E] mt-1">Backup management, DR runbook, multi-region health, observability config</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={loadAll}><RefreshCw className="w-4 h-4 mr-1" /> Refresh</Button>
          <Button onClick={createBackup} disabled={creatingBackup} className="bg-[#121316] text-white hover:bg-[#1B1D22] text-sm">
            <Database className="w-4 h-4 mr-2" />{creatingBackup ? 'Creating…' : 'Create Backup'}
          </Button>
        </div>
      </div>

      {/* Multi-region health */}
      {multiRegion && (
        <div className={`flex items-center gap-3 p-4 rounded-xl border ${multiRegion.overall === 'healthy' ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
          <Globe className={`w-5 h-5 ${multiRegion.overall === 'healthy' ? 'text-green-600' : 'text-amber-600'}`} />
          <div>
            <p className="text-sm font-medium">Multi-Region Status: <span className={multiRegion.overall === 'healthy' ? 'text-green-700' : 'text-amber-700'}>{multiRegion.overall.toUpperCase()}</span></p>
            {multiRegion.regions.map(r => (
              <span key={r.region} className="text-xs text-[#5B616E] mr-3">{r.region}: {r.status}</span>
            ))}
          </div>
          <Badge className="ml-auto text-xs">{multiRegion.dr_mode} mode</Badge>
        </div>
      )}

      <Tabs defaultValue="backups">
        <TabsList>
          <TabsTrigger value="backups"><Database className="w-4 h-4 mr-1" /> Backups</TabsTrigger>
          <TabsTrigger value="runbook"><Shield className="w-4 h-4 mr-1" /> DR Runbook</TabsTrigger>
          <TabsTrigger value="observability"><Activity className="w-4 h-4 mr-1" /> Observability</TabsTrigger>
        </TabsList>

        <TabsContent value="backups" className="mt-4">
          {loading ? <Skeleton className="h-48" /> : (
            <Card className="bg-white border-[#E7E0D6]">
              <CardContent className="pt-4">
                {backups.length === 0 ? (
                  <div className="text-center py-8 text-[#5B616E]">
                    <Database className="w-12 h-12 mx-auto mb-3 opacity-20" />
                    <p>No backups yet. Click "Create Backup" to take a snapshot.</p>
                  </div>
                ) : backups.map(b => (
                  <div key={b.id} className="flex items-center justify-between p-3 border border-[#E7E0D6] rounded-lg mb-2">
                    <div>
                      <p className="text-sm font-medium font-mono">{b.backup_id}</p>
                      <p className="text-xs text-[#5B616E]">{b.total_documents?.toLocaleString()} documents · {b.created_at ? new Date(b.created_at).toLocaleString() : ''}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className="text-[10px] bg-green-100 text-green-700">{b.status}</Badge>
                      <Button size="sm" variant="outline" className="text-xs" onClick={async () => {
                        const r = await apiPost(`/api/system/restore/${b.backup_id}`);
                        toast.info(r.status + ': ' + (r.note || ''));
                      }}>
                        <Upload className="w-3 h-3 mr-1" /> Restore
                      </Button>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="runbook" className="mt-4">
          {runbook ? (
            <Card className="bg-white border-[#E7E0D6]">
              <CardHeader>
                <CardTitle className="text-sm">{runbook.title}</CardTitle>
                <p className="text-xs text-[#5B616E]">RTO: {runbook.rto_minutes}min · RPO: {runbook.rpo_hours}h</p>
              </CardHeader>
              <CardContent className="space-y-4">
                {runbook.scenarios?.map((s, i) => (
                  <div key={i} className="p-3 bg-[#FBF7EF] rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle className="w-4 h-4 text-amber-500" />
                      <p className="text-sm font-medium">{s.scenario}</p>
                    </div>
                    <ol className="space-y-0.5">
                      {s.steps.map((step, j) => (
                        <li key={j} className="text-xs text-[#5B616E] font-mono">{step}</li>
                      ))}
                    </ol>
                  </div>
                ))}
                <div className="text-xs text-[#5B616E] mt-3">
                  <p><strong>Backup schedule:</strong> <code className="font-mono">{runbook.backup_schedule}</code></p>
                  <p><strong>On-call:</strong> {runbook.contacts?.on_call} · <strong>Escalation:</strong> {runbook.contacts?.escalation}</p>
                </div>
              </CardContent>
            </Card>
          ) : <Skeleton className="h-64" />}
        </TabsContent>

        <TabsContent value="observability" className="mt-4">
          {obsConfig ? (
            <div className="space-y-4">
              {['prometheus', 'grafana', 'alerting', 'logging', 'tracing'].map(section => (
                <Card key={section} className="bg-white border-[#E7E0D6]">
                  <CardHeader><CardTitle className="text-sm capitalize">{section}</CardTitle></CardHeader>
                  <CardContent>
                    <pre className="text-xs text-[#5B616E] bg-[#FBF7EF] p-3 rounded-lg overflow-x-auto whitespace-pre-wrap">
                      {JSON.stringify(obsConfig[section], null, 2)}
                    </pre>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : <Skeleton className="h-64" />}
        </TabsContent>
      </Tabs>
    </div>
  );
}
